import Swal from 'sweetalert2';

/** Clases premium compartidas (SweetAlert2) — alineadas con CRM / Gestión Derivación. */
export const CRM_SWAL_CUSTOM_CLASS = {
  container: 'swal-crm-container',
  popup: 'swal-crm-popup',
  title: 'swal-crm-title',
  htmlContainer: 'swal-crm-html',
  icon: 'swal-crm-icon',
  actions: 'swal-crm-actions',
  confirmButton: 'swal-crm-confirm',
  cancelButton: 'swal-crm-cancel'
};

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const ICON_LOCK = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    <circle cx="12" cy="15.4" r="1.35" fill="currentColor" stroke="none" />
  </svg>
`;

const ICON_EYE = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M2.5 12S5.5 5.5 12 5.5 21.5 12 21.5 12 18.5 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
`;

const ICON_EYE_OFF = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 3l18 18" />
    <path d="M10.6 6.1A9.7 9.7 0 0 1 12 6c6.5 0 9.5 6 9.5 6a16 16 0 0 1-3.2 3.9" />
    <path d="M6.2 7.7A15.9 15.9 0 0 0 2.5 12S5.5 18 12 18a9.4 9.4 0 0 0 3.3-.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
`;

function mergeCustomClass(overrides = {}, icon = 'question') {
  const popupMods = ['swal-crm-popup'];
  if (icon) {
    const iconKind = String(icon).toLowerCase();
    if (iconKind === 'warning') popupMods.push('swal-crm-popup--warning');
    if (iconKind === 'error') popupMods.push('swal-crm-popup--danger');
    if (iconKind === 'success') popupMods.push('swal-crm-popup--success');
  }

  const extraPopup = String(overrides?.popup || '').trim();
  const popup = [popupMods.join(' '), extraPopup].filter(Boolean).join(' ');

  return {
    ...CRM_SWAL_CUSTOM_CLASS,
    ...(overrides || {}),
    popup
  };
}

/**
 * Cuerpo HTML compacto para confirmaciones CRM.
 * @param {{ lead?: string, items?: string[], footnote?: string, details?: { label: string, value: string }[] }} opts
 */
export function buildCrmSwalBody({ lead = '', items = [], footnote = '', details = [] } = {}) {
  const leadHtml = lead ? `<p class="crm-swal-lead">${lead}</p>` : '';
  const detailsHtml =
    Array.isArray(details) && details.length
      ? `<dl class="crm-swal-details">${details
          .map(
            (row) =>
              `<div class="crm-swal-detail"><dt>${escapeHtml(row.label)}</dt><dd>${escapeHtml(row.value)}</dd></div>`
          )
          .join('')}</dl>`
      : '';
  const list =
    Array.isArray(items) && items.length
      ? `<ul class="crm-swal-list">${items.map((line) => `<li>${line}</li>`).join('')}</ul>`
      : '';
  const foot = footnote ? `<p class="crm-swal-footnote">${footnote}</p>` : '';
  return `<div class="crm-swal-body">${leadHtml}${detailsHtml}${list}${foot}</div>`;
}

/** HTML libre (admin, avisos técnicos) con tipografía CRM. */
export function wrapCrmSwalRich(innerHtml) {
  return `<div class="crm-swal-body"><div class="crm-swal-rich">${innerHtml}</div></div>`;
}

const API_KEY_SUCCESS_ICON_HTML = `
  <div class="crm-swal-success-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" width="36" height="36">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" fill="rgba(16, 185, 129, 0.12)" />
      <path
        d="M7.75 12.25l2.5 2.5 5.5-5.5"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
`;

/** Modal único al crear API key (clave visible una sola vez). */
export function showApiKeyGeneratedDialog({ apiKey = '', expiryLabel = '' } = {}) {
  const keyHtml = escapeHtml(apiKey);
  const expiryHtml = escapeHtml(expiryLabel);

  return fireConfirmDialog({
    title: 'API key generada',
    html: `${API_KEY_SUCCESS_ICON_HTML}${wrapCrmSwalRich(`
      <p>Copiala ahora. Por seguridad <strong>no se vuelve a mostrar</strong>.</p>
      <p class="crm-swal-api-expiry"><strong>Vence:</strong> ${expiryHtml}</p>
      <code class="crm-swal-api-key">${keyHtml}</code>
    `)}`,
    icon: false,
    confirmButtonText: 'Copiar y cerrar',
    cancelButtonText: 'Cerrar',
    allowOutsideClick: false,
    width: 'min(420px, 92vw)'
  });
}

export function fireConfirmDialog(options = {}) {
  const {
    customClass,
    confirmButtonColor,
    cancelButtonColor,
    width,
    padding,
    text,
    html,
    icon,
    zIndex = 5000,
    didOpen,
    ...restOptions
  } = options || {};

  let resolvedHtml = html;
  if (!resolvedHtml && text) {
    resolvedHtml = buildCrmSwalBody({ lead: String(text) });
  }

  const resolvedIcon = icon ?? restOptions.icon;
  const iconForClass = resolvedIcon === false ? null : (resolvedIcon ?? 'question');

  const swalConfig = {
    showCancelButton: true,
    confirmButtonText: 'Confirmar',
    cancelButtonText: 'Cancelar',
    reverseButtons: true,
    buttonsStyling: true,
    focusConfirm: false,
    heightAuto: true,
    width: width ?? 'min(380px, 92vw)',
    padding: padding ?? undefined,
    confirmButtonColor: confirmButtonColor || '#2365ab',
    cancelButtonColor: cancelButtonColor || '#e2e8f0',
    customClass: mergeCustomClass(customClass, iconForClass),
    html: resolvedHtml,
    text: undefined,
    zIndex,
    didOpen: () => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = String(zIndex);
      const popup = Swal.getPopup();
      if (popup) popup.style.zIndex = String(zIndex + 1);
      if (typeof didOpen === 'function') didOpen();
    },
    ...restOptions
  };

  if (resolvedIcon !== false) {
    swalConfig.icon = resolvedIcon ?? 'question';
  }

  return Swal.fire(swalConfig);
}

/** Aviso informativo (un solo botón) con el mismo shell premium que las confirmaciones CRM. */
export function fireCrmAlert(options = {}) {
  const {
    customClass,
    confirmButtonColor,
    width,
    padding,
    text,
    html,
    icon,
    confirmButtonText = 'Entendido',
    zIndex = 4000,
    didOpen,
    ...restOptions
  } = options || {};

  let resolvedHtml = html;
  if (!resolvedHtml && text) {
    resolvedHtml = buildCrmSwalBody({ lead: String(text) });
  }

  const resolvedIcon = icon === false ? false : (icon ?? 'warning');
  const iconForClass = resolvedIcon === false ? null : resolvedIcon;

  const swalConfig = {
    showCancelButton: false,
    confirmButtonText,
    buttonsStyling: true,
    focusConfirm: true,
    heightAuto: true,
    width: width ?? 'min(380px, 92vw)',
    padding: padding ?? undefined,
    confirmButtonColor: confirmButtonColor || '#2365ab',
    customClass: mergeCustomClass(customClass, iconForClass),
    html: resolvedHtml,
    text: undefined,
    zIndex,
    didOpen: () => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = String(zIndex);
      const popup = Swal.getPopup();
      if (popup) popup.style.zIndex = String(zIndex + 1);
      if (typeof didOpen === 'function') didOpen();
    },
    ...restOptions
  };

  if (resolvedIcon !== false) {
    swalConfig.icon = resolvedIcon;
  }

  return Swal.fire(swalConfig);
}

/**
 * Indicador de pasos premium (1 → 2) para flujos multi-step en SweetAlert.
 * @param {number} active Paso activo (1 o 2).
 */
export function buildCrmSwalSteps(active = 1) {
  const stepOneClass = active > 1 ? 'is-done' : 'is-active';
  const stepOneLabel = active > 1 ? '✓' : '1';
  const stepTwoClass = active >= 2 ? 'is-active' : '';
  const barClass = active >= 2 ? 'is-active' : '';
  return `
    <div class="crm-swal-steps" aria-hidden="true">
      <span class="crm-swal-steps__dot ${stepOneClass}">${stepOneLabel}</span>
      <span class="crm-swal-steps__bar ${barClass}"></span>
      <span class="crm-swal-steps__dot ${stepTwoClass}">2</span>
    </div>
  `;
}

/**
 * Diálogo premium de confirmación por contraseña (campo custom con candado y mostrar/ocultar).
 * Devuelve el resultado de SweetAlert; `result.value` contiene la contraseña ingresada.
 * @param {{
 *   title?: string,
 *   lead?: string,
 *   hint?: string,
 *   stepsHtml?: string,
 *   expectedPassword?: string,
 *   inputLabel?: string,
 *   placeholder?: string,
 *   confirmButtonText?: string,
 *   cancelButtonText?: string,
 *   width?: string
 * }} options
 */
export function firePasswordConfirmDialog(options = {}) {
  const {
    title = 'Confirmá tu identidad',
    lead = '',
    hint = '',
    stepsHtml = '',
    expectedPassword = '',
    inputLabel = 'Contraseña de administrador',
    placeholder = '••••••••',
    confirmButtonText = 'Confirmar',
    cancelButtonText = 'Cancelar',
    width
  } = options || {};

  const leadHtml = lead ? `<p class="crm-swal-lead">${lead}</p>` : '';
  const hintHtml = hint
    ? `<p class="crm-swal-pass__hint"><span class="crm-swal-pass__hint-dot" aria-hidden="true"></span>${escapeHtml(hint)}</p>`
    : '';

  const html = `
    <div class="crm-swal-body crm-swal-body--pass">
      ${stepsHtml}
      <div class="crm-swal-pass-badge" aria-hidden="true">${ICON_LOCK}</div>
      ${leadHtml}
      <div class="crm-swal-pass">
        <label class="crm-swal-pass__label" for="crmSwalPassInput">${escapeHtml(inputLabel)}</label>
        <div class="crm-swal-pass__field">
          <span class="crm-swal-pass__icon" aria-hidden="true">${ICON_LOCK}</span>
          <input
            id="crmSwalPassInput"
            name="crm-confirm-secret"
            type="password"
            class="crm-swal-pass__input"
            placeholder="${escapeHtml(placeholder)}"
            autocomplete="new-password"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />
          <button type="button" class="crm-swal-pass__toggle" data-crm-pass-toggle aria-label="Mostrar contraseña">
            <span class="crm-swal-pass__eye crm-swal-pass__eye--show">${ICON_EYE}</span>
            <span class="crm-swal-pass__eye crm-swal-pass__eye--hide">${ICON_EYE_OFF}</span>
          </button>
        </div>
        ${hintHtml}
      </div>
    </div>
  `;

  return Swal.fire({
    title,
    html,
    icon: undefined,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
    reverseButtons: true,
    buttonsStyling: true,
    focusConfirm: false,
    heightAuto: true,
    width: width ?? 'min(420px, 92vw)',
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#e2e8f0',
    customClass: mergeCustomClass({ popup: 'swal-crm-popup--danger' }, null),
    didOpen: () => {
      const popup = Swal.getPopup();
      if (!popup) return;
      const input = popup.querySelector('#crmSwalPassInput');
      const toggle = popup.querySelector('[data-crm-pass-toggle]');
      if (input) {
        setTimeout(() => input.focus(), 60);
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            Swal.clickConfirm();
          }
        });
        input.addEventListener('input', () => {
          Swal.resetValidationMessage();
        });
      }
      if (toggle && input) {
        toggle.addEventListener('click', () => {
          const willShow = input.type === 'password';
          input.type = willShow ? 'text' : 'password';
          toggle.classList.toggle('is-visible', willShow);
          toggle.setAttribute('aria-label', willShow ? 'Ocultar contraseña' : 'Mostrar contraseña');
          input.focus();
        });
      }
    },
    preConfirm: () => {
      const popup = Swal.getPopup();
      const input = popup?.querySelector('#crmSwalPassInput');
      const value = input ? input.value : '';
      if (!value) {
        Swal.showValidationMessage('Ingresá la contraseña para confirmar.');
        return false;
      }
      if (expectedPassword && value !== expectedPassword) {
        Swal.showValidationMessage('Contraseña incorrecta.');
        return false;
      }
      return value;
    }
  });
}
